import type { AppContextEntry } from '../../chat-app-context'
import { describeEntity, describeFilters, formatCount } from '../../chat-app-context'
import type { SortMode, SourceFilter } from './WorkOrdersPage'

export interface WorkOrdersAppContextInput {
  /** Expanded work order, when one is open (`/work-orders/:id`). */
  expanded?: {
    /** First instruction line — the closest thing a work order has to a title. */
    title: string
    status: string
    priority: string
    id: string
  }
  status: string
  /** Empty when every priority is listed. */
  priorities: readonly string[]
  source: SourceFilter
  sort: SortMode
  search: string
  /** Rows the current filters resolve to. */
  listedCount: number
}

/**
 * The technician inbox keeps its filters on the URL, so the baseline
 * already offers them raw. This registration names them and turns the
 * expanded `:id` into `title + status + priority + id`.
 */
export function buildWorkOrdersAppContextEntries(
  input: WorkOrdersAppContextInput
): AppContextEntry[] {
  const entries: AppContextEntry[] = []

  if (input.expanded !== undefined) {
    entries.push({
      key: 'Work order',
      value: describeEntity({
        name: input.expanded.title,
        fields: [
          ['status', input.expanded.status],
          ['priority', input.expanded.priority]
        ],
        id: input.expanded.id
      })
    })
  }

  entries.push({
    key: 'Filters',
    value: describeFilters([
      ['status', input.status],
      ['priority', input.priorities.length > 0 ? input.priorities.join(', ') : null],
      ['source', input.source === 'all' ? null : input.source],
      ['sort', input.sort],
      ['search', input.search.trim()],
      ['listed', formatCount(input.listedCount, 'work order')]
    ])
  })

  return entries
}

/** Baseline path / search params the entries above stand in for. */
export const WORK_ORDERS_SUPERSEDED_PARAMS = ['id', 'status', 'priority', 'source', 'sort', 'q']
